'use client';

import React from 'react';
import { Button, ButtonProps } from '@/components/ui/button';
import { ConfirmDialogCds } from '@/components/cds/confirm-dialog-cds';

export type ButtonConfirmCdsProps = {
  label: string | React.ReactNode;
  title?: string | React.ReactNode;
  description: string | React.ReactNode;
  disabled?: boolean;
  variant?: ButtonProps['variant'];
  classNameConfirm?: string;
  classNameCancel?: string;
  confirmLabel?: string | React.ReactNode;
  cancelLabel?: string | React.ReactNode;
  onConfirm: () => void;
  onCancel?: () => void;
};

/**
 * A component that renders a button which triggers an confirm dialog.
 *
 * @param {Object} props - The properties object.
 * @param {string} props.label - The label for the button.
 * @param {string} [props.title] - The title for the confirm dialog.
 * @param {string} props.description - The description for the confirm dialog.
 * @param {boolean} [props.disabled] - Whether the button is disabled.
 * @param {string} [props.variant] - The variant of the button.
 * @param {string} [props.classNameConfirm] - The class name for the confirm button.
 * @param {string} [props.classNameCancel] - The class name for the cancel button.
 * @param {string} [props.confirmLabel] - The label for the confirm button.
 * @param {string} [props.cancelLabel] - The label for the cancel button.
 * @param {Function} props.onConfirm - The function to call when the confirm button is clicked.
 * @param {Function} [props.onCancel] - The function to call when the cancel button is clicked.
 *
 * @returns {JSX.Element} The rendered button confirm component.
 */
export function ButtonConfirmCds({
  label,
  title,
  description,
  disabled,
  variant,
  classNameConfirm,
  classNameCancel,
  confirmLabel,
  cancelLabel,
  onConfirm,
  onCancel,
}: Readonly<ButtonConfirmCdsProps>) {
  return (
    <ConfirmDialogCds
      title={title || label}
      description={description}
      classNameConfirm={classNameConfirm}
      classNameCancel={classNameCancel}
      confirmLabel={confirmLabel}
      cancelLabel={cancelLabel}
      button={
        <Button variant={variant} disabled={!!disabled}>
          {label}
        </Button>
      }
      onConfirm={onConfirm}
      onCancel={onCancel}
    />
  );
}
